import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { db } from '../../firebase';
import {
  doc,
  getDoc,
  collection,
  addDoc,
  updateDoc,
  getDocs,
  serverTimestamp,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  runTransaction,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getCardStats } from '../../utilities';
import {
  Typography,
  Button,
  TextField,
  Box,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import { PlayingCard } from '../PlayingCard';

// ---- Beasts of Legend style tokens ----
const palette = {
  bg: 'linear-gradient(180deg, #000 0%, #0a0a0a 100%)',
  card: 'rgba(0,0,0,0.7)',
  gold: '#d4af37',
  goldSoft: 'rgba(212,175,55,0.75)',
  goldDim: 'rgba(212,175,55,0.6)',
  text: '#f9f6e3',
  border: 'rgba(212,175,55,0.35)',
  shadow: '0 10px 30px rgba(212,175,55,0.18)',
};

const goldBorder = {
  border: `1px solid ${palette.border}`,
  borderRadius: 10, // ⬅️ slightly less round than before
  boxShadow: palette.shadow,
};

const goldButton = {
  px: 2.5,
  py: 1,
  borderRadius: 10,
  textTransform: 'none',
  letterSpacing: '0.6px',
  fontWeight: 600,
  color: '#0b0b0b',
  background: `linear-gradient(180deg, ${palette.goldSoft} 0%, ${palette.gold} 100%)`,
  '&:hover': {
    boxShadow: '0 0 0 3px rgba(212,175,55,0.25)',
    background: `linear-gradient(180deg, ${palette.gold} 0%, ${palette.gold} 100%)`,
  },
};

const ghostButton = {
  ...goldBorder,
  color: '#0b0b0b',
  background: `linear-gradient(180deg, ${palette.goldSoft} 0%, ${palette.gold} 100%)`,
  '&:hover': {
    boxShadow: '0 0 0 3px rgba(212,175,55,0.25)',
    background: `linear-gradient(180deg, ${palette.gold} 0%, ${palette.gold} 100%)`,
  },
  borderColor: palette.goldDim,
  textTransform: 'none',
  letterSpacing: '0.6px',
};

const dangerButton = {
  ...goldButton,
  background: 'linear-gradient(180deg, rgba(212,75,55,0.75) 0%, rgba(212,75,55,0.95) 100%)',
};

// Hard cap
const GUILD_MEMBER_CAP = 50;

// How many guilds to show at a time in search/suggestions
const SUGGESTED_GUILD_COUNT = 10;

// Join request cooldown (client-side anti-spam)
const JOIN_REQUEST_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const JOIN_REQUEST_LS_KEY = 'bol_joinRequests_v1';

/**
 * ---------- Gen2 Cloud Run URLs (direct) ----------
 */
const getEnv = (k) => {
  if (typeof import.meta !== 'undefined' && import.meta.env && k in import.meta.env) return import.meta.env[k];
  if (typeof process !== 'undefined' && process.env && k in process.env) return process.env[k];
  return undefined;
};

const isDev =
  (typeof import.meta !== 'undefined' && import.meta.env && !!import.meta.env.DEV) ||
  (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development');

const PROJECT_ID = 'beasts-of-legend-bc9bd';
const REGION = 'us-central1';

const CF_BASES = {
  requestGuildHelp:
    getEnv('VITE_CF_REQUEST_GUILD_HELP') ||
    getEnv('REACT_APP_CF_REQUEST_GUILD_HELP') ||
    (isDev
      ? `http://127.0.0.1:5001/${PROJECT_ID}/${REGION}/requestGuildHelp`
      : 'https://us-central1-beasts-of-legend-bc9bd.cloudfunctions.net/requestGuildHelp'),
  assistGuildHelp:
    getEnv('VITE_CF_ASSIST_GUILD_HELP') ||
    getEnv('REACT_APP_CF_ASSIST_GUILD_HELP') ||
    (isDev
      ? `http://127.0.0.1:5001/${PROJECT_ID}/${REGION}/assistGuildHelp`
      : 'https://us-central1-beasts-of-legend-bc9bd.cloudfunctions.net/assistGuildHelp'),
  joinPublicGuild:
    getEnv('VITE_CF_JOIN_PUBLIC_GUILD') ||
    getEnv('REACT_APP_CF_JOIN_PUBLIC_GUILD') ||
    (isDev
      ? `http://127.0.0.1:5001/${PROJECT_ID}/${REGION}/joinPublicGuild`
      : 'https://us-central1-beasts-of-legend-bc9bd.cloudfunctions.net/joinPublicGuild'),
  // ✅ private guild join request (Admin SDK)
  requestJoinPrivateGuild:
    getEnv('VITE_CF_REQUEST_JOIN_PRIVATE_GUILD') ||
    getEnv('REACT_APP_CF_REQUEST_JOIN_PRIVATE_GUILD') ||
    (isDev
      ? `http://127.0.0.1:5001/${PROJECT_ID}/${REGION}/requestJoinPrivateGuild`
      : 'https://us-central1-beasts-of-legend-bc9bd.cloudfunctions.net/requestJoinPrivateGuild'),
  guildLeaderboard:
    getEnv('VITE_CF_GUILD_LEADERBOARD') ||
    getEnv('REACT_APP_CF_GUILD_LEADERBOARD') ||
    (isDev
      ? `http://127.0.0.1:5001/${PROJECT_ID}/${REGION}/guildLeaderboard`
      : 'https://us-central1-beasts-of-legend-bc9bd.cloudfunctions.net/guildLeaderboard'),
  approveJoinRequestPrivateGuild:
    getEnv('VITE_CF_APPROVE_JOIN_PRIVATE_GUILD') ||
    getEnv('REACT_APP_CF_APPROVE_JOIN_PRIVATE_GUILD') ||
    (isDev
      ? `http://127.0.0.1:5001/${PROJECT_ID}/${REGION}/approveJoinRequestPrivateGuild`
      : 'https://us-central1-beasts-of-legend-bc9bd.cloudfunctions.net/approveJoinRequestPrivateGuild'),
  denyJoinRequestPrivateGuild:
    getEnv('VITE_CF_DENY_JOIN_PRIVATE_GUILD') ||
    getEnv('REACT_APP_CF_DENY_JOIN_PRIVATE_GUILD') ||
    (isDev
      ? `http://127.0.0.1:5001/${PROJECT_ID}/${REGION}/denyJoinRequestPrivateGuild`
      : 'https://us-central1-beasts-of-legend-bc9bd.cloudfunctions.net/denyJoinRequestPrivateGuild'),
};

// Auth'd fetch helper for HTTPS Functions — with timeout & richer errors
async function authedFetchAbs(url, method = 'POST', body = {}, { timeoutMs = 15000 } = {}) {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Not authenticated.');

  const idToken = await currentUser.getIdToken(false);
  const controller = new AbortController();
  const to = setTimeout(() => controller.abort(), timeoutMs);

  let res;
  try {
    res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      credentials: 'omit',
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(to);
    if (err?.name === 'AbortError') throw new Error('Request timed out.');
    throw new Error('Network error.');
  } finally {
    clearTimeout(to);
  }

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.error?.message) msg += ` – ${j.error.message}`;
      else if (j?.error) msg += ` – ${j.error}`;
    } catch {
      // ignore
    }
    throw new Error(msg || 'Request failed.');
  }

  return res.json().catch(() => ({}));
}

// ---------- Local hydration helpers (mirror of your utilities) ----------
const hydrateCard = (instance, globalCards) => {
  if (!instance || !globalCards) return null;
  const base = globalCards[instance.cardId];
  if (!base) return null;
  const evo = base.evolutions?.[instance.evolutionStage || 0] || {};
  return {
    ...base,
    ...instance,
    image: evo.image || base.image,
    rarity: evo.rarity || base.rarity,
    ability: evo.ability || base.ability,
    abilityDescription: evo.abilityDescription || base.abilityDescription,
    displayName: evo.name || base.name || base.id || instance.cardId,
    name: evo.name || base.name || base.id || instance.cardId,
  };
};

const hydrateInstances = (instances = [], globalCards = {}) =>
  instances.map((inst) => hydrateCard(inst, globalCards)).filter(Boolean);

const hasUserAssisted = (request, uid) => {
  if (!uid || !request) return false;
  const arrays = [request.assisterIds, request.assistants, request.assistedBy, request.helperIds].filter(Array.isArray);
  if (arrays.length) {
    const set = new Set(arrays.flat().map(String));
    if (set.has(String(uid))) return true;
  }
  if (request.lastAssistantId === uid || request.helperUid === uid || request.assistantUid === uid) return true;
  return false;
};

const isUsersRequest = (request, uid) => {
  if (!uid || !request) return false;
  const candidates = [
    request.requesterId,
    request.requesterUid,
    request.ownerUid,
    request.ownerId,
    request.userUid,
    request.userId,
    request.uid,
    request.instanceOwnerUid,
    request.createdBy,
  ];
  return candidates
    .filter((v) => v !== undefined && v !== null)
    .map(String)
    .includes(String(uid));
};

const getRandomGuildSubset = (guilds, count) => {
  if (!Array.isArray(guilds) || guilds.length <= count) return guilds || [];
  const arr = [...guilds];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr.slice(0, count);
};

// ---------- Members MAP helpers ----------
function normalizeMembersMap(members) {
  if (members && typeof members === 'object' && !Array.isArray(members)) return members;
  return {};
}

function getMemberCount(guildDoc) {
  if (!guildDoc) return 0;
  if (typeof guildDoc.memberCount === 'number') return guildDoc.memberCount;
  const members = normalizeMembersMap(guildDoc.members);
  return Object.keys(members).length;
}

function membersToDisplayList(membersMap) {
  const m = normalizeMembersMap(membersMap);
  return Object.entries(m).map(([uid, v]) => ({
    uid,
    username: v?.username,
    role: v?.role || 'Member',
  }));
}

// ---------- LocalStorage join-request cooldown ----------
function safeParseJSON(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function readJoinRequestCache() {
  if (typeof window === 'undefined') return {};
  const raw = window.localStorage.getItem(JOIN_REQUEST_LS_KEY);
  const parsed = safeParseJSON(raw);
  if (!parsed || typeof parsed !== 'object') return {};
  return parsed;
}

function writeJoinRequestCache(cacheObj) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(JOIN_REQUEST_LS_KEY, JSON.stringify(cacheObj || {}));
  } catch {
    // ignore
  }
}

function getJoinCooldownRemainingMs(guildId) {
  if (!guildId) return 0;
  const cache = readJoinRequestCache();
  const last = Number(cache[guildId] || 0);
  if (!Number.isFinite(last) || last <= 0) return 0;
  const rem = last + JOIN_REQUEST_COOLDOWN_MS - Date.now();
  return rem > 0 ? rem : 0;
}

function markJoinRequestedNow(guildId) {
  if (!guildId) return;
  const cache = readJoinRequestCache();
  cache[guildId] = Date.now();
  writeJoinRequestCache(cache);
}

function fmtDuration(ms) {
  const totalMin = Math.max(0, Math.ceil(ms / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

// ---------- PublicUser helpers (POWER = sum of DEF of defenseDeckPublic) ----------
function getUserTrophies(d) {
  return Number(d?.trophies ?? d?.trophyCount ?? d?.arenaTrophies ?? d?.totalTrophies ?? 0) || 0;
}

function computeDefenseDeckPowerFromPublicUser(publicUserDoc, globalCardsById) {
  const deck = Array.isArray(publicUserDoc?.defenseDeckPublic) ? publicUserDoc.defenseDeckPublic : [];
  if (!deck.length) return 0;

  let totalDef = 0;

  for (const inst of deck) {
    const cardId = inst?.cardId;
    if (!cardId) continue;

    const base = globalCardsById?.[cardId];
    if (!base) continue;

    // getCardStats expects (instance, baseCard)
    const instanceForStats = {
      cardId,
      level: Number(inst?.level || 1),
      evolutionStage: Number(inst?.evolutionStage || 0),
      ascensions: Number(inst?.ascensions || 0),
    };

    const stats = getCardStats(instanceForStats, base);
    const def = Number(stats?.defense ?? stats?.def ?? 0) || 0;
    totalDef += def;
  }

  return totalDef;
}

// ---------- Component ----------
export default function GuildIndex() {
  const { userData, setUserData } = useContext(UserContext);

  const [guild, setGuild] = useState(null);
  const [guilds, setGuilds] = useState([]);
  const [displayGuilds, setDisplayGuilds] = useState([]);
  const [guildSearchTerm, setGuildSearchTerm] = useState('');

  const [guildLeaderboardData, setGuildLeaderboardData] = useState(null);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newGuildName, setNewGuildName] = useState('');
  const [newGuildPublic, setNewGuildPublic] = useState(true);

  // We keep error state for debugging/logging, but we do NOT render it directly to users.
  const [error, setError] = useState(null);

  // Snackbars (user-friendly feedback)
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const notify = useCallback((message, severity = 'success') => {
    setSnack({ open: true, message: String(message || ''), severity });
  }, []);

  // Help system local state
  const [myOwnedCards, setMyOwnedCards] = useState([]);
  const [helpRequests, setHelpRequests] = useState([]);
  const [requesting, setRequesting] = useState(false);
  const [selectedInstanceId, setSelectedInstanceId] = useState('');
  const [helpBusy, setHelpBusy] = useState(null);

  // "now" clock so we can hide expired help requests in real time + refresh cooldown disables
  const [now, setNow] = useState(() => Date.now());

  // global cards cache (by id), hydrated views
  const [globalCardsById, setGlobalCardsById] = useState({});

  // selection modal state
  const [selectOpen, setSelectOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [rarity, setRarity] = useState('All');
  const [pendingSelection, setPendingSelection] = useState(selectedInstanceId);

  // Fallback guild totals cache (when leaderboard totals are missing)
  const [guildTotalsFallback, setGuildTotalsFallback] = useState({}); // { [guildId]: { totalTrophies, totalPower } }

  const uid = userData?.uid;
  const username = userData?.username;

  const membersMap = useMemo(() => normalizeMembersMap(guild?.members), [guild?.members]);
  const membersList = useMemo(() => membersToDisplayList(membersMap), [membersMap]);

  const myRole = useMemo(() => {
    if (!guild || !uid) return null;
    const me = membersMap?.[uid];
    return me?.role || null;
  }, [guild, uid, membersMap]);

  const isLeader = useMemo(() => guild?.leaderUid === uid, [guild, uid]);
  const canToggleVisibility = useMemo(() => isLeader, [isLeader]);

  // tick "now" every 60s
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Fetch global cards for hydration (and power calc)
  useEffect(() => {
    const loadCards = async () => {
      try {
        const snap = await getDocs(collection(db, 'cards'));
        const byId = {};
        snap.forEach((d) => {
          const data = d.data() || {};
          const id = data.id || d.id;
          if (id) byId[id] = { id, ...data };
        });
        setGlobalCardsById(byId);
      } catch (e) {
        console.warn('Failed to load cards:', e?.message || e);
      }
    };
    loadCards();
  }, []);

  // Load my guild
  useEffect(() => {
    const loadGuild = async () => {
      try {
        setError(null);
        if (!userData?.guildId) return;
        const guildRef = doc(db, 'guilds', userData.guildId);
        const guildSnap = await getDoc(guildRef);
        if (guildSnap.exists()) setGuild({ id: guildSnap.id, ...guildSnap.data() });
        else setGuild(null);
      } catch (e) {
        console.error('loadGuild error', e);
        setError(e?.message || String(e));
      }
    };
    loadGuild();
  }, [userData?.guildId]);

  // List all guilds (for browsing / search)
  useEffect(() => {
    const loadAllGuilds = async () => {
      try {
        setError(null);
        const snapshot = await getDocs(collection(db, 'guilds'));
        const allGuilds = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setGuilds(allGuilds);
        setDisplayGuilds(getRandomGuildSubset(allGuilds, SUGGESTED_GUILD_COUNT));
      } catch (e) {
        console.error('loadAllGuilds error', e);
        setError(e?.message || String(e));
      }
    };
    loadAllGuilds();
  }, []);

  // Load guild leaderboard
  useEffect(() => {
    const loadLeaderboard = async () => {
      try {
        // ✅ include memberUids in the request body
        const memberUids =
          userData?.guildId && guild?.members ? Object.keys(normalizeMembersMap(guild.members)) : undefined;

        const body = userData?.guildId ? { guildId: userData.guildId, memberUids } : {};

        const result = await authedFetchAbs(CF_BASES.guildLeaderboard, 'POST', body);
        setGuildLeaderboardData(result || null);
      } catch (e) {
        console.error('loadLeaderboard error', e);
        // keep quiet for users
      }
    };
    loadLeaderboard();
  }, [userData?.guildId, guild?.members]);

  // Load my owned cards for "Ask for Help" dropdown (modal)
  useEffect(() => {
    const run = async () => {
      try {
        if (!uid) return;
        const snap = await getDoc(doc(db, 'users', uid));
        if (snap.exists()) {
          const d = snap.data() || {};
          setMyOwnedCards(Array.isArray(d.ownedCards) ? d.ownedCards : []);
        }
      } catch (e) {
        console.warn('load myOwnedCards error', e?.message || e);
      }
    };
    run();
  }, [uid]);

  // Live Help Board (open requests in my guild)
  useEffect(() => {
    if (!guild?.id) return;
    const colRef = collection(db, 'guilds', guild.id, 'helpRequests');
    const qy = query(colRef, where('open', '==', true), orderBy('createdAt', 'desc'), limit(100));

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setHelpRequests(rows);
      },
      (err) => {
        console.error('[helpRequests] onSnapshot error:', err);
      }
    );
    return () => unsub && unsub();
  }, [guild?.id]);

  // Create guild (members is MAP now)
  const handleCreateGuild = async () => {
    if (!newGuildName || !uid || !username) return;
    try {
      setError(null);

      const members = {
        [uid]: { username, role: 'General' },
      };

      const guildRef = await addDoc(collection(db, 'guilds'), {
        name: newGuildName,
        members,
        memberCount: 1,
        leaderUid: uid,
        leaderUsername: username,
        isPublic: !!newGuildPublic,
        createdAt: serverTimestamp(),
      });

      await updateDoc(doc(db, 'users', uid), { guildId: guildRef.id });

      setUserData((prev) => ({ ...prev, guildId: guildRef.id }));
      setShowCreateDialog(false);
      setNewGuildName('');
      setNewGuildPublic(true);

      const createdGuild = {
        id: guildRef.id,
        name: newGuildName,
        members,
        memberCount: 1,
        leaderUid: uid,
        leaderUsername: username,
        isPublic: !!newGuildPublic,
      };

      setGuild(createdGuild);
      setGuilds((prev) => [...prev, createdGuild]);
      notify('Guild created!', 'success');
    } catch (e) {
      console.error('handleCreateGuild error', e);
      setError(e?.message || String(e));
      notify('Could not create guild. Please try again.', 'warning');
    }
  };

  // Public join via HTTPS function (Admin SDK)
  const handleJoinGuild = async (guildId) => {
    if (!uid || !username) return;
    try {
      setError(null);
      await authedFetchAbs(CF_BASES.joinPublicGuild, 'POST', { guildId, username });

      const s = await getDoc(doc(db, 'guilds', guildId));
      if (s.exists()) {
        const joinedGuild = { id: s.id, ...s.data() };
        setGuild(joinedGuild);
        setUserData((prev) => ({ ...prev, guildId }));
      }
      notify('Joined guild!', 'success');
    } catch (e) {
      console.error('handleJoinGuild error', e);
      setError(e?.message || String(e));
      notify('Could not join. Please try again.', 'warning');
    }
  };

  // ✅ UPDATED: Private join request via HTTPS function (Admin SDK) + LocalStorage anti-spam
  const handleRequestJoin = async (guildId) => {
    const authUid2 = getAuth().currentUser?.uid || null;
    if (!authUid2) return;

    const guildObj = guilds.find((g) => g.id === guildId);
    if (guildObj?.isPublic !== false) return;

    if (uid && authUid2 !== uid) {
      console.warn('[GuildIndex] UID mismatch', { authUid: authUid2, userDataUid: uid });
      notify('Please refresh and try again.', 'info');
      return;
    }

    const rem = getJoinCooldownRemainingMs(guildId);
    if (rem > 0) {
      notify(`Request already sent. Try again in ${fmtDuration(rem)}.`, 'info');
      return;
    }

    try {
      setError(null);
      await authedFetchAbs(CF_BASES.requestJoinPrivateGuild, 'POST', { guildId });
      markJoinRequestedNow(guildId);
      notify('Request sent! The General will review it.', 'success');
    } catch (e) {
      console.error('handleRequestJoin error', e);
      setError(e?.message || String(e));
      notify('Could not send request. Please try again later.', 'warning');
    }
  };

  // Visibility toggle (LEADER ONLY per rules)
  const handleTogglePublic = async () => {
    if (!guild?.id) return;
    if (!isLeader) return;

    try {
      setError(null);
      const guildRef = doc(db, 'guilds', guild.id);
      await updateDoc(guildRef, { isPublic: !guild.isPublic });
      setGuild((prev) => ({ ...prev, isPublic: !prev.isPublic }));
      notify('Guild visibility updated.', 'success');
    } catch (e) {
      console.error('togglePublic error', e);
      setError(e?.message || String(e));
      notify('Could not update visibility.', 'warning');
    }
  };

  // Promote/Demote to Captain (General only) — MEMBERS MAP
  const setCaptain = async (targetUid, makeCaptain) => {
    if (!guild?.id || !isLeader) return;
    try {
      setError(null);

      const members = { ...normalizeMembersMap(guild.members) };
      const target = members[targetUid];
      if (!target) return;
      if (targetUid === guild.leaderUid) return;

      members[targetUid] = { ...target, role: makeCaptain ? 'Captain' : 'Member' };

      await updateDoc(doc(db, 'guilds', guild.id), {
        members,
        memberCount: Object.keys(members).length,
      });

      setGuild((prev) => ({ ...prev, members, memberCount: Object.keys(members).length }));
      notify(makeCaptain ? 'Promoted to Captain.' : 'Demoted to Member.', 'success');
    } catch (e) {
      console.error('setCaptain error', e);
      setError(e?.message || String(e));
      notify('Could not update role.', 'warning');
    }
  };

  // Transfer General (General -> target). Current General becomes Captain. — MEMBERS MAP
  const transferGeneral = async (targetUid) => {
    if (!guild?.id || !isLeader) return;
    try {
      setError(null);

      const members = { ...normalizeMembersMap(guild.members) };
      const currentLeaderUid = guild.leaderUid;
      const target = members[targetUid];
      const currentLeader = members[currentLeaderUid];
      if (!target || !currentLeader) return;

      members[targetUid] = { ...target, role: 'General' };
      members[currentLeaderUid] = { ...currentLeader, role: 'Captain' };

      await updateDoc(doc(db, 'guilds', guild.id), {
        leaderUid: targetUid,
        leaderUsername: target.username,
        members,
        memberCount: Object.keys(members).length,
      });

      setGuild((prev) => ({
        ...prev,
        leaderUid: targetUid,
        leaderUsername: target.username,
        members,
        memberCount: Object.keys(members).length,
      }));

      notify('General transferred.', 'success');
    } catch (e) {
      console.error('transferGeneral error', e);
      setError(e?.message || String(e));
      notify('Could not transfer General.', 'warning');
    }
  };

  // Leave guild (Members/Captains). General cannot leave—must disband or transfer first.
  const handleLeaveGuild = async () => {
    if (!guild?.id || !uid) return;
    if (isLeader) return;

    try {
      setError(null);

      const guildRef = doc(db, 'guilds', guild.id);

      await runTransaction(db, async (tx) => {
        const snap = await tx.get(guildRef);
        if (!snap.exists()) throw new Error('Guild not found.');
        const data = snap.data() || {};
        const members = { ...normalizeMembersMap(data.members) };
        if (!members[uid]) throw new Error('Not a member.');
        delete members[uid];

        tx.update(guildRef, {
          members,
          memberCount: Object.keys(members).length,
        });

        tx.update(doc(db, 'users', uid), { guildId: null });
      });

      setUserData((prev) => ({ ...prev, guildId: null }));
      setGuild(null);
      notify('Left guild.', 'success');
    } catch (e) {
      console.error('leaveGuild error', e);
      setError(e?.message || String(e));
      notify('Could not leave guild.', 'warning');
    }
  };

  // Disband guild (General only).
  const handleDisbandGuild = async () => {
    if (!guild?.id || !isLeader) return;
    try {
      setError(null);
      await deleteDoc(doc(db, 'guilds', guild.id));
      try {
        await updateDoc(doc(db, 'users', uid), { guildId: null });
      } catch {
        // ignore
      }
      setUserData((prev) => ({ ...prev, guildId: null }));
      setGuild(null);
      notify('Guild disbanded.', 'success');
    } catch (e) {
      console.error('disbandGuild error', e);
      setError(e?.message || String(e));
      notify('Could not disband guild.', 'warning');
    }
  };

  // Join requests (leader only)
  const [joinRequests, setJoinRequests] = useState([]);

  useEffect(() => {
    const loadRequests = async () => {
      try {
        setJoinRequests([]);
        if (!guild?.id || !isLeader) return;

        const snap = await getDocs(collection(db, 'guilds', guild.id, 'joinRequests'));
        const reqs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setJoinRequests(reqs);
      } catch (e) {
        console.error('loadRequests error', e);
        setError(e?.message || String(e));
      }
    };
    loadRequests();
  }, [guild?.id, isLeader]);

  // Approve/Deny request (LEADER ONLY)
  const approveRequest = async (request) => {
    if (!guild?.id || !isLeader) return;

    try {
      await authedFetchAbs(CF_BASES.approveJoinRequestPrivateGuild, 'POST', {
        guildId: guild.id,
        requestId: request.id,
      });

      const snap = await getDoc(doc(db, 'guilds', guild.id));
      if (snap.exists()) setGuild({ id: snap.id, ...snap.data() });

      setJoinRequests((prev) => prev.filter((r) => r.id !== request.id));

      notify('Request approved.', 'success');
    } catch (e) {
      console.error('approveRequest error', e);
      notify('Could not approve request.', 'warning');
    }
  };

  const denyRequest = async (request) => {
    if (!guild?.id || !isLeader) return;

    try {
      await authedFetchAbs(CF_BASES.denyJoinRequestPrivateGuild, 'POST', {
        guildId: guild.id,
        requestId: request.id,
      });

      setJoinRequests((prev) => prev.filter((r) => r.id !== request.id));
      notify('Request denied.', 'info');
    } catch (e) {
      console.error('denyRequest error', e);
      notify('Could not deny request.', 'warning');
    }
  };

  // Help system handlers
  async function submitHelpRequest() {
    if (!selectedInstanceId || !guild?.id) return;
    setRequesting(true);
    try {
      await authedFetchAbs(CF_BASES.requestGuildHelp, 'POST', {
        guildId: guild.id,
        instanceId: selectedInstanceId,
      });
      setSelectedInstanceId('');
      notify('Help request posted!', 'success');
    } catch (e) {
      console.error('submitHelpRequest error', e);
      notify('Could not post help request.', 'warning');
    } finally {
      setRequesting(false);
    }
  }

  async function assist(requestId) {
    if (!guild?.id) return;
    setHelpBusy(requestId);
    try {
      await authedFetchAbs(CF_BASES.assistGuildHelp, 'POST', {
        guildId: guild.id,
        requestId,
      });
      notify('Assist sent!', 'success');
    } catch (e) {
      console.error('assist error', e);
      notify('Could not assist.', 'warning');
    } finally {
      setHelpBusy(null);
    }
  }

  // ---------- Hydrated & deduped data for UI ----------
  const hydratedOwned = useMemo(() => hydrateInstances(myOwnedCards, globalCardsById), [myOwnedCards, globalCardsById]);

  const dedupedOwned = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of hydratedOwned) {
      const id = c?.instanceId;
      if (id && !seen.has(id)) {
        seen.add(id);
        out.push(c);
      }
    }
    return out;
  }, [hydratedOwned]);

  const RARITY_ORDER = useMemo(
    () => ({
      Mythic: 8,
      Legendary: 7,
      'Super Super Rare': 6,
      'Super Rare': 5,
      'High Rare': 4,
      Rare: 3,
      Uncommon: 2,
      Common: 1,
    }),
    []
  );

  const filteredCards = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    return dedupedOwned
      .filter((c) => (rarity === 'All' ? true : String(c?.rarity) === rarity))
      .filter((c) => {
        if (!q) return true;
        const name = String(c?.name || c?.displayName || c?.cardId || '').toLowerCase();
        return name.includes(q);
      })
      .sort((a, b) => {
        const ra = RARITY_ORDER[String(a?.rarity || 'Common')] || 0;
        const rb = RARITY_ORDER[String(b?.rarity || 'Common')] || 0;
        if (rb !== ra) return rb - ra;
        const la = Number(a?.level || 1);
        const lb = Number(b?.level || 1);
        if (lb !== la) return lb - la;
        return String(a.name || a.cardId).localeCompare(String(b.name || b.cardId));
      });
  }, [dedupedOwned, search, rarity, RARITY_ORDER]);

  const hydratedHelpRequests = useMemo(() => {
    const isActive = (req) => {
      const exp = req.expiresAt;
      if (!exp) return true;

      let expMs;
      if (typeof exp?.toMillis === 'function') expMs = exp.toMillis();
      else if (typeof exp?.seconds === 'number') expMs = exp.seconds * 1000;
      else expMs = new Date(exp).getTime();

      if (!Number.isFinite(expMs)) return true;
      return expMs > now;
    };

    return helpRequests
      .filter(isActive)
      .map((r) => {
        const inst = {
          cardId: r.cardId,
          instanceId: r.instanceId,
          level: r.levelAtRequest || 1,
          evolutionStage: r.evolutionStage || 0,
        };
        const hydrated = hydrateCard(inst, globalCardsById);
        return { ...r, hydrated };
      });
  }, [helpRequests, globalCardsById, now]);

  const fmtXP = (xp) => (typeof xp === 'number' ? ` • XP ${xp}` : '');

  const openSelectModal = () => {
    setPendingSelection(selectedInstanceId || '');
    setSelectOpen(true);
  };
  const confirmSelection = () => {
    setSelectedInstanceId(pendingSelection || '');
    setSelectOpen(false);
  };
  const clearSelection = () => {
    setSelectedInstanceId('');
    setPendingSelection('');
  };

  const selectedCard = useMemo(
    () => dedupedOwned.find((c) => c.instanceId === selectedInstanceId) || null,
    [dedupedOwned, selectedInstanceId]
  );

  const memberCount = useMemo(() => getMemberCount(guild), [guild]);

  const myGuildRanking = useMemo(() => {
    if (!guild || !guildLeaderboardData) return null;
    if (guildLeaderboardData.self?.guildId === guild.id) return guildLeaderboardData.self;
    return (guildLeaderboardData.leaderboard || []).find((gEntry) => gEntry.guildId === guild.id) || null;
  }, [guild, guildLeaderboardData]);

  const getGuildRanking = (guildId) => {
    if (!guildLeaderboardData || !guildId) return null;
    const self = guildLeaderboardData.self;
    if (self?.guildId === guildId) return self;
    return (guildLeaderboardData.leaderboard || []).find((gEntry) => gEntry.guildId === guildId) || null;
  };

  // ✅ Fallback: compute totals from members map, with POWER = sum(defenseDeckPublic DEF)
  const computeGuildTotalsFallback = useCallback(
    async (guildId, members) => {
      if (!guildId || !members) return;
      if (guildTotalsFallback[guildId]) return;

      const memberUids = Object.keys(normalizeMembersMap(members));
      if (!memberUids.length) return;

      // If we don't have cards yet, defer (we'll retry via effects when cards load)
      if (!globalCardsById || Object.keys(globalCardsById).length === 0) return;

      try {
        const snaps = await Promise.all(memberUids.map((mUid) => getDoc(doc(db, 'publicUsers', mUid))));

        let totalTrophies = 0;
        let totalPower = 0;

        for (const s of snaps) {
          if (!s.exists()) continue;
          const d = s.data() || {};
          totalTrophies += getUserTrophies(d);
          totalPower += computeDefenseDeckPowerFromPublicUser(d, globalCardsById);
        }

        setGuildTotalsFallback((prev) => ({
          ...prev,
          [guildId]: { totalTrophies, totalPower },
        }));
      } catch (e) {
        console.warn('computeGuildTotalsFallback error', e);
      }
    },
    [guildTotalsFallback, globalCardsById]
  );

  // Kick off fallback totals for *my guild* if leaderboard totals are missing/zero-ish
  useEffect(() => {
    if (!guild?.id) return;
    const r = myGuildRanking;
    const missingTotals =
      !r ||
      typeof r.totalTrophies !== 'number' ||
      typeof r.totalPower !== 'number' ||
      (r.totalTrophies === 0 && r.totalPower === 0);

    if (missingTotals) {
      computeGuildTotalsFallback(guild.id, guild.members);
    }
  }, [guild?.id, guild?.members, myGuildRanking, computeGuildTotalsFallback]);

  // ✅ Precompute fallback totals for currently displayed guild cards (avoid setState during render)
  useEffect(() => {
    if (!displayGuilds?.length) return;
    if (!guildLeaderboardData) return; // only show "outside top 1,000" / missing logic when leaderboard exists
    if (!globalCardsById || Object.keys(globalCardsById).length === 0) return;

    const toCompute = [];

    for (const g of displayGuilds) {
      const count =
        typeof g.memberCount === 'number' ? g.memberCount : Object.keys(normalizeMembersMap(g.members)).length;

      const isPublicGuild = g.isPublic !== false;
      const rankInfo = getGuildRanking(g.id);

      const totalsMissing =
        !rankInfo ||
        typeof rankInfo.totalTrophies !== 'number' ||
        typeof rankInfo.totalPower !== 'number' ||
        (rankInfo.totalTrophies === 0 && rankInfo.totalPower === 0);

      // Only compute fallback for private guilds (public totals likely in leaderboard), and only if not cached
      if (!isPublicGuild && totalsMissing && !guildTotalsFallback[g.id]) {
        // still safe for up to 50 members; your cap is 50
        toCompute.push(g);
      } else if (isPublicGuild && totalsMissing && !guildTotalsFallback[g.id] && count <= 15) {
        // optional small-guild assist for public ones too
        toCompute.push(g);
      }
    }

    if (toCompute.length === 0) return;

    toCompute.forEach((g) => {
      computeGuildTotalsFallback(g.id, g.members);
    });
  }, [displayGuilds, guildLeaderboardData, globalCardsById, guildTotalsFallback, computeGuildTotalsFallback]);

  const handleGuildSearch = () => {
    const term = (guildSearchTerm || '').trim().toLowerCase();
    if (!term) {
      setDisplayGuilds(getRandomGuildSubset(guilds, SUGGESTED_GUILD_COUNT));
      return;
    }
    const filtered = guilds
      .filter((g) => String(g.name || '').toLowerCase().includes(term))
      .slice(0, SUGGESTED_GUILD_COUNT);
    setDisplayGuilds(filtered);
  };

  const handleRefreshGuildSuggestions = () => {
    setGuildSearchTerm('');
    setDisplayGuilds(getRandomGuildSubset(guilds, SUGGESTED_GUILD_COUNT));
  };

  // For rendering: pick leaderboard totals, otherwise fallback totals if available
  const myGuildTotals = useMemo(() => {
    if (!guild?.id) return null;
    const r = myGuildRanking;

    if (
      r &&
      typeof r.totalTrophies === 'number' &&
      typeof r.totalPower === 'number' &&
      (r.totalTrophies !== 0 || r.totalPower !== 0)
    ) {
      return { totalTrophies: r.totalTrophies, totalPower: r.totalPower, rank: r.rank };
    }

    const fb = guildTotalsFallback[guild.id];
    if (fb) return { ...fb, rank: r?.rank };

    return null;
  }, [guild?.id, myGuildRanking, guildTotalsFallback]);

  // Bigger, airier guild search cards
  const guildSearchCardSx = {
    background: palette.card,
    border: `1px solid ${palette.border}`,
    borderRadius: 8, // ⬅️ less round
    boxShadow: '0 14px 34px rgba(212,175,55,0.16)',
    minHeight: 190,
    transition: 'box-shadow 160ms, transform 160ms',
    '&:hover': {
      boxShadow: '0 0 0 2px rgba(212,175,55,0.28), 0 18px 42px rgba(212,175,55,0.20)',
      transform: 'translateY(-2px)',
    },
  };

  const guildSearchCardContentSx = {
    p: 2.0, // ⬅️ more padding
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: palette.bg, color: palette.text }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" sx={{ fontFamily: 'Cinzel, serif', letterSpacing: '1px', color: palette.gold }}>
            Guilds
          </Typography>
          <Chip
            label="Beasts of Legend"
            sx={{ color: palette.text, borderColor: palette.goldDim, ...goldBorder }}
            variant="outlined"
          />
        </Box>

        <Button sx={goldButton} onClick={() => setLeaderboardOpen(true)} disabled={!guildLeaderboardData}>
          {guildLeaderboardData ? 'Guild Leaderboard' : 'Loading Leaderboard…'}
        </Button>
      </Box>

      {/* In a guild */}
      {guild ? (
        <Card sx={{ mb: 4, background: palette.card, ...goldBorder }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ fontFamily: 'Cinzel, serif', color: palette.gold }}>
                {guild.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip
                  label={guild.isPublic ? 'Public' : 'Private'}
                  sx={{ borderColor: palette.goldDim, color: palette.text, ...goldBorder }}
                  variant="outlined"
                />
                {canToggleVisibility && (
                  <FormControlLabel
                    sx={{ m: 0, color: palette.text }}
                    control={<Switch checked={!!guild.isPublic} onChange={handleTogglePublic} />}
                    label={guild.isPublic ? 'Set Private' : 'Set Public'}
                  />
                )}
              </Box>
            </Box>

            {myGuildTotals?.rank ? (
              <Typography variant="body2" sx={{ mt: 0.5, color: palette.text }}>
                Rank: <span style={{ color: palette.gold }}>#{myGuildTotals.rank}</span> • Trophies:{' '}
                <span style={{ color: palette.gold }}>{myGuildTotals.totalTrophies ?? '—'}</span> • Power:{' '}
                <span style={{ color: palette.gold }}>{myGuildTotals.totalPower ?? '—'}</span>
              </Typography>
            ) : myGuildTotals ? (
              <Typography variant="body2" sx={{ mt: 0.5, color: palette.text }}>
                Trophies: <span style={{ color: palette.gold }}>{myGuildTotals.totalTrophies ?? '—'}</span> • Power:{' '}
                <span style={{ color: palette.gold }}>{myGuildTotals.totalPower ?? '—'}</span>
              </Typography>
            ) : guildLeaderboardData ? (
              <Typography variant="body2" sx={{ mt: 0.5, color: palette.goldDim }}>
                This guild is currently outside the top 1,000.
              </Typography>
            ) : null}

            <Typography variant="body2" sx={{ color: palette.text, mt: 0.5 }}>
              Leader: <span style={{ color: palette.gold }}>{guild.leaderUsername}</span>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: palette.text }}>
              Members ({memberCount}/{GUILD_MEMBER_CAP})
            </Typography>

            <Box component="ul" sx={{ mt: 1.5, pl: 3 }}>
              {membersList.map((m) => {
                const canGeneralAct = isLeader && m.uid !== guild.leaderUid;
                return (
                  <Box key={m.uid} component="li" sx={{ color: palette.text, mb: 1 }}>
                    <span style={{ color: palette.gold }}>{m.username}</span> — <em>{m.role || 'Member'}</em>
                    {canGeneralAct && (
                      <Box sx={{ display: 'inline-flex', gap: 1, ml: 1.5 }}>
                        {m.role === 'Captain' ? (
                          <Button sx={ghostButton} size="small" onClick={() => setCaptain(m.uid, false)}>
                            Demote to Member
                          </Button>
                        ) : (
                          <Button sx={ghostButton} size="small" onClick={() => setCaptain(m.uid, true)}>
                            Promote to Captain
                          </Button>
                        )}
                        <Button sx={goldButton} size="small" onClick={() => transferGeneral(m.uid)}>
                          Make General
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            <Divider sx={{ my: 2, borderColor: palette.border }} />

            {/* ---------- Guild Help — Leveling Boon ---------- */}
            <Box sx={{ display: 'grid', gap: 1.5, mb: 2 }}>
              <Typography variant="h6" sx={{ fontFamily: 'Cinzel, serif', color: palette.gold }}>
                Guild Help — Leveling Boon
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {selectedCard ? (
                  <Chip
                    label={`${selectedCard.name || selectedCard.displayName || selectedCard.cardId} — Lv ${
                      selectedCard.level || 1
                    }${fmtXP(selectedCard.xp)}`}
                    onDelete={clearSelection}
                    sx={{ color: palette.text, borderColor: palette.goldDim, ...goldBorder }}
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="body2" sx={{ color: palette.goldDim }}>
                    No card selected
                  </Typography>
                )}

                <Button sx={goldButton} onClick={openSelectModal}>
                  {selectedCard ? 'Change Card' : 'Choose Card'}
                </Button>

                <Button sx={goldButton} disabled={!selectedInstanceId || requesting} onClick={submitHelpRequest}>
                  {requesting ? 'Requesting…' : 'Ask Guild for Help'}
                </Button>

                <Typography variant="body2" sx={{ color: palette.text }}>
                  You can ask once every 24 hours.
                </Typography>
              </Box>
            </Box>

            {/* ---------- Help Board ---------- */}
            <Box>
              <Typography variant="h6" sx={{ fontFamily: 'Cinzel, serif', color: palette.gold, mb: 1 }}>
                Help Board (last 24h)
              </Typography>

              {hydratedHelpRequests.length === 0 ? (
                <Typography variant="body2" sx={{ color: palette.text }}>
                  No active requests.
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {hydratedHelpRequests.map((r) => {
                    const mine = isUsersRequest(r, uid);
                    const h = r.hydrated;
                    const alreadyAssisted = hasUserAssisted(r, uid);

                    let assistLabel = 'Assist (+5% XP)';
                    let assistDisabled = false;
                    let assistStyle = goldButton;

                    if (mine) {
                      assistLabel = 'Your Card';
                      assistDisabled = true;
                      assistStyle = ghostButton;
                    } else if (alreadyAssisted) {
                      assistLabel = 'Assisted';
                      assistDisabled = true;
                      assistStyle = ghostButton;
                    } else if (helpBusy === r.id) {
                      assistLabel = 'Assisting…';
                      assistDisabled = true;
                      assistStyle = goldButton;
                    }

                    return (
                      <Box
                        key={r.id}
                        sx={{
                          p: 1.25,
                          background: '#111',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 1,
                          borderRadius: 8,
                          border: `1px solid ${palette.border}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <Box sx={{ position: 'relative' }}>
                            {h ? (
                              <Box sx={{ ml: 5,width: 88 }}>
                                <PlayingCard card={h} compact />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  width: 88,
                                  height: 124,
                                  borderRadius: 1.5,
                                  border: `1px solid ${palette.border}`,
                                  background: '#0f0f0f',
                                }}
                              />
                            )}
                          </Box>

                          <Box sx={{ ml: 16 }}>
                            <Typography sx={{ color: palette.text }}>
                              <span style={{ color: palette.gold }}>{r.requesterUsername}</span> needs help leveling{' '}
                              <span style={{ color: palette.gold }}>{h?.name || h?.displayName || r.cardId}</span>
                            </Typography>
                            <Typography variant="caption" sx={{ color: palette.goldDim }}>
                              Asked:{' '}
                              {r.levelAtRequest ? `Lv ${r.levelAtRequest}` : h?.level ? `Lv ${h.level}` : '—'} • Assists:{' '}
                              {r.assistsCount || 0}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, mr: 5 }}>
                          <Button sx={assistStyle} disabled={assistDisabled} onClick={() => assist(r.id)}>
                            {assistLabel}
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>

            <Divider sx={{ my: 2, borderColor: palette.border }} />

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {!isLeader && (
                <Button sx={ghostButton} onClick={handleLeaveGuild}>
                  Leave Guild
                </Button>
              )}
              {isLeader && (
                <Button sx={dangerButton} onClick={handleDisbandGuild}>
                  Disband Guild
                </Button>
              )}
            </Box>

            {isLeader && (
              <>
                <Divider sx={{ my: 2, borderColor: palette.border }} />
                <Typography variant="h6" sx={{ fontFamily: 'Cinzel, serif', color: palette.gold, mb: 1 }}>
                  Join Requests
                </Typography>
                {joinRequests.length === 0 ? (
                  <Typography variant="body2" sx={{ color: palette.text }}>
                    No pending requests.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {joinRequests.map((req) => (
                      <Box
                        key={req.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.25,
                          background: '#111',
                          border: `1px solid ${palette.border}`,
                          borderRadius: 8,
                        }}
                      >
                        <Typography sx={{ color: palette.text }}>
                          <span style={{ color: palette.gold }}>{req.username}</span> wants to join.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button sx={goldButton} onClick={() => approveRequest(req)}>
                            Approve
                          </Button>
                          <Button sx={ghostButton} onClick={() => denyRequest(req)}>
                            Deny
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        // Not in a guild
        <>
          <Card sx={{ mb: 3, background: palette.card, ...goldBorder }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: palette.text, mb: 1 }}>
                You are not in a guild.
              </Typography>
              <Button sx={goldButton} onClick={() => setShowCreateDialog(true)}>
                Create Guild
              </Button>
            </CardContent>
          </Card>

          {/* Guild Search / Suggestions */}
          <Card sx={{ mb: 3, background: palette.card, ...goldBorder }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Typography variant="h6" sx={{ mt: 0.5, mb: 0.5, fontFamily: 'Cinzel, serif', color: palette.gold }}>
                  Find a Guild
                </Typography>
                <Typography variant="body2" sx={{ color: palette.text }}>
                  Showing up to {SUGGESTED_GUILD_COUNT} guilds
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <TextField
                  label="Search by Guild Name"
                  variant="filled"
                  size="small"
                  value={guildSearchTerm}
                  onChange={(e) => setGuildSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleGuildSearch();
                  }}
                  InputProps={{ sx: { color: palette.text, background: '#121212' } }}
                  InputLabelProps={{ sx: { color: palette.goldDim } }}
                  sx={{ minWidth: 260 }}
                />
                <Button sx={goldButton} onClick={handleGuildSearch}>
                  Search
                </Button>
                <Button sx={ghostButton} onClick={handleRefreshGuildSuggestions}>
                  Refresh Suggestions
                </Button>
              </Box>

              {displayGuilds.length === 0 ? (
                <Typography variant="body2" sx={{ color: palette.text }}>
                  No guilds found. Try a different search or refresh suggestions.
                </Typography>
              ) : (
                <Grid container spacing={2.5}>
                  {displayGuilds.map((g) => {
                    const count =
                      typeof g.memberCount === 'number' ? g.memberCount : Object.keys(normalizeMembersMap(g.members)).length;

                    const full = count >= GUILD_MEMBER_CAP;
                    const isPublicGuild = g.isPublic !== false;
                    const rankInfo = getGuildRanking(g.id);
                    const fb = guildTotalsFallback[g.id];

                    const rem = !isPublicGuild ? getJoinCooldownRemainingMs(g.id) : 0;
                    const canRequest = !isPublicGuild && rem <= 0;

                    const showTotalsFromLeaderboard =
                      rankInfo &&
                      typeof rankInfo.totalTrophies === 'number' &&
                      typeof rankInfo.totalPower === 'number' &&
                      (rankInfo.totalTrophies !== 0 || rankInfo.totalPower !== 0);

                    return (
                      <Grid item key={g.id} xs={12} sm={6} md={4} lg={3}>
                        <Card sx={guildSearchCardSx}>
                          <CardContent sx={guildSearchCardContentSx}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box sx={{ pr: 1 }}>
                                <Typography
                                  sx={{
                                    color: palette.text,
                                    fontWeight: 700,
                                    fontSize: '1.05rem',
                                    lineHeight: 1.15,
                                  }}
                                >
                                  {g.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.goldDim }}>
                                  Leader: <span style={{ color: palette.text }}>{g.leaderUsername || '—'}</span>
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                {rankInfo?.rank ? (
                                  <Chip
                                    size="small"
                                    label={`#${rankInfo.rank}`}
                                    sx={{ borderColor: palette.goldDim, color: palette.text, borderRadius: 8 }}
                                    variant="outlined"
                                  />
                                ) : null}
                                <Chip
                                  size="small"
                                  label={isPublicGuild ? 'Public' : 'Private'}
                                  sx={{ borderColor: palette.goldDim, color: palette.text, borderRadius: 8 }}
                                  variant="outlined"
                                />
                              </Box>
                            </Box>

                            <Typography variant="body2" sx={{ color: palette.text }}>
                              Members: <span style={{ color: palette.gold }}>{count}</span> / {GUILD_MEMBER_CAP}
                            </Typography>

                            {showTotalsFromLeaderboard ? (
                              <Typography variant="body2" sx={{ mt: 0.5, color: palette.goldDim }}>
                                Trophies: <span style={{ color: palette.text }}>{rankInfo.totalTrophies}</span> • Power:{' '}
                                <span style={{ color: palette.text }}>{rankInfo.totalPower}</span>
                              </Typography>
                            ) : fb ? (
                              <Typography variant="body2" sx={{ mt: 0.5, color: palette.goldDim }}>
                                Trophies: <span style={{ color: palette.text }}>{fb.totalTrophies}</span> • Power:{' '}
                                <span style={{ color: palette.text }}>{fb.totalPower}</span>
                              </Typography>
                            ) : guildLeaderboardData ? (
                              <Typography variant="body2" sx={{ mt: 0.5, color: palette.goldDim }}>
                                Outside top 1,000
                              </Typography>
                            ) : null}

                            {isPublicGuild ? (
                              <Button
                                sx={{ ...goldButton, mt: 1.6, width: '100%' }}
                                disabled={full}
                                onClick={() => handleJoinGuild(g.id)}
                              >
                                {full ? 'Full' : 'Join'}
                              </Button>
                            ) : (
                              <Button
                                sx={{ ...ghostButton, mt: 1.6, width: '100%' }}
                                disabled={!canRequest}
                                onClick={() => handleRequestJoin(g.id)}
                              >
                                {canRequest ? 'Request to Join' : `Requested (${fmtDuration(rem)})`}
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Create Guild Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        PaperProps={{ sx: { background: '#0b0b0b', ...goldBorder } }}
      >
        <DialogTitle sx={{ fontFamily: 'Cinzel, serif', color: palette.gold }}>Create Guild</DialogTitle>
        <DialogContent>
          <TextField
            label="Guild Name"
            fullWidth
            variant="filled"
            InputProps={{ sx: { color: palette.text, background: '#121212' } }}
            InputLabelProps={{ sx: { color: palette.goldDim } }}
            sx={{ mt: 1 }}
            value={newGuildName}
            onChange={(e) => setNewGuildName(e.target.value)}
          />

          <FormControlLabel
            sx={{ mt: 1, color: palette.text }}
            control={<Switch checked={newGuildPublic} onChange={(e) => setNewGuildPublic(e.target.checked)} />}
            label={newGuildPublic ? 'Public (anyone can join)' : 'Private (requests only)'}
          />

          <Box sx={{ display: 'flex', gap: 1.5, mt: 2, mb: 1 }}>
            <Button sx={goldButton} onClick={handleCreateGuild}>
              Create
            </Button>
            <Button sx={ghostButton} onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Guild Leaderboard Dialog */}
      <Dialog
        open={leaderboardOpen}
        onClose={() => setLeaderboardOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { background: '#0b0b0b', ...goldBorder } }}
      >
        <DialogTitle sx={{ fontFamily: 'Cinzel, serif', color: palette.gold }}>Guild Leaderboard — Top 1,000</DialogTitle>
        <DialogContent dividers>
          {!guildLeaderboardData ? (
            <Typography variant="body2" sx={{ color: palette.text }}>
              Loading leaderboard…
            </Typography>
          ) : (guildLeaderboardData.leaderboard || []).length === 0 ? (
            <Typography variant="body2" sx={{ color: palette.text }}>
              No guilds ranked yet.
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 480, overflowY: 'auto', pr: 1 }}>
              {guildLeaderboardData.leaderboard.map((entry) => {
                const isMyGuild = guild?.id === entry.guildId;

                return (
                  <Box
                    key={entry.guildId}
                    sx={{
                      p: 1.2,
                      mb: 1,
                      borderRadius: 1.2,
                      background: isMyGuild ? 'rgba(212,175,55,0.1)' : '#111',
                      border: isMyGuild ? `1px solid ${palette.gold}` : `1px solid ${palette.border}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography sx={{ color: palette.text, fontWeight: 600 }}>
                        #{entry.rank} — <span style={{ color: palette.gold }}>{entry.name}</span>
                      </Typography>
                      <Typography variant="caption" sx={{ color: palette.goldDim }}>
                        Members: {entry.memberCount} • Trophies: {entry.totalTrophies} • Power: {entry.totalPower}
                      </Typography>
                    </Box>

                    {!guild && entry.memberCount < GUILD_MEMBER_CAP ? (
                      <Button
                        sx={goldButton}
                        onClick={() => {
                          setLeaderboardOpen(false);
                          handleJoinGuild(entry.guildId);
                        }}
                      >
                        Join
                      </Button>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button sx={ghostButton} onClick={() => setLeaderboardOpen(false)}>
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Card Selection Modal */}
      <Dialog
        open={selectOpen}
        onClose={() => setSelectOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            background: '#0b0b0b',
            ...goldBorder,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Cinzel, serif', color: palette.gold }}>Select a Card to Request Help</DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              placeholder="Search by name…"
              size="small"
              variant="filled"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ sx: { color: palette.text, background: '#121212' } }}
              InputLabelProps={{ sx: { color: palette.goldDim } }}
            />
            <TextField
              select
              size="small"
              label="Rarity"
              variant="filled"
              SelectProps={{ native: true }}
              value={rarity}
              onChange={(e) => setRarity(e.target.value)}
              InputProps={{ sx: { color: palette.text, background: '#121212' } }}
              InputLabelProps={{ sx: { color: palette.goldDim } }}
            >
              {['All', 'Legendary', 'Super Super Rare', 'Super Rare', 'High Rare', 'Rare', 'Uncommon', 'Common'].map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </TextField>
          </Box>

          {filteredCards.length === 0 ? (
            <Alert severity="info" sx={{ ...goldBorder, background: '#111', color: palette.text }}>
              No cards match your filters.
            </Alert>
          ) : (
            <Grid container spacing={1.5}>
              {filteredCards.map((c) => {
                const selected = pendingSelection === c.instanceId;
                return (
                  <Grid item key={`inst:${c.instanceId}`}>
                    <Card
                      sx={{
                        width: 220,
                        background: palette.card,
                        position: 'relative',
                        transition: 'box-shadow 160ms, transform 160ms',
                        ...(selected
                          ? { boxShadow: '0 0 0 2px rgba(212,175,55,0.6), 0 10px 30px rgba(212,175,55,0.28)' }
                          : { '&:hover': { boxShadow: '0 0 0 2px rgba(212,175,55,0.35)' } }),
                        paddingRight: 2.5,
                      }}
                    >
                      <CardContent sx={{ p: 1.25 }}>
                        <Box sx={{ position: 'relative', mb: 1 }}>
                          <Box sx={{ width: '100%', mb: 4 }}>
                            <PlayingCard card={c} compact />
                          </Box>
                        </Box>

                        <Typography sx={{ color: palette.text, fontWeight: 600 }}>{c.name || c.displayName || c.cardId}</Typography>

                        <Typography variant="body2" sx={{ color: palette.goldDim }}>
                          {c.rarity || 'Common'} • Lv {c.level || 1}
                          {fmtXP(c.xp)}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            sx={{
                              ...ghostButton,
                              ...(selected ? { borderColor: palette.gold, color: 'black' } : {}),
                            }}
                            size="small"
                            onClick={() => setPendingSelection(c.instanceId)}
                          >
                            {selected ? 'Selected' : 'Select'}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, p: 2 }}>
          <Button sx={ghostButton} onClick={() => setSelectOpen(false)}>
            Cancel
          </Button>
          <Button sx={goldButton} disabled={!pendingSelection} onClick={confirmSelection}>
            Use This Card
          </Button>
        </Box>
      </Dialog>

      {/* Snackbar feedback (no raw error blocks on the page) */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3200}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((p) => ({ ...p, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{
            background: snack.severity === 'success' ? 'rgba(212,175,55,0.95)' : '#222',
            color: snack.severity === 'success' ? '#0b0b0b' : palette.text,
            border: `1px solid ${palette.border}`,
          }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
